import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type TreeNode } from '../lib/api.js';
import { MODES, type Mode } from './Shell.js';

/**
 * Command palette on ⌘K.
 *
 * A clause number is the fastest way into an 87-page instrument, and someone
 * who already knows they want 24.9.1(b) should not have to walk an outline to
 * reach it. Matching is anchored so typing "19" offers clause 19 before
 * clause 19.4 and never before clause 119 — the same discipline as citation
 * matching, for the same reason.
 *
 * That covers numbers and headings, and it covered only those for months. A
 * teacher looking for what the agreement says about being asked to cover
 * someone else's class does not know it is clause 19, and the heading reads
 * "Ordinary Hours of Work". Typing "meal break" found nothing, while the
 * clause that grants it — number 20, heading "Breaks" — sat there with the
 * words in its body.
 *
 * The index for that has existed since the corpus was built: `clause.tsv`,
 * with a GIN index over it, read by nothing but Ask's retriever. So the words
 * were searchable and nothing offered to search them.
 *
 * Local matches still come first and instantly. The text results arrive when
 * they arrive and appear beneath, because a palette that waits on a network
 * round trip before showing anything is slower at the thing it was already
 * good at.
 */
export function Palette({
  nodes,
  onClause,
  onMode,
  onClose,
}: {
  nodes: TreeNode[];
  onClause: (number: string) => void;
  onMode: (mode: Mode) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return MODES.map((mode) => ({ kind: 'mode' as const, label: mode, hint: 'Go to' }));
    }

    const clauses = nodes
      .map((node) => {
        const number = node.number.toLowerCase();
        const heading = (node.heading ?? '').toLowerCase();
        // Exact number, then number prefix, then heading — a citation beats a
        // word match, because someone typing a number knows what they want.
        const score = number === q ? 0 : number.startsWith(q) ? 1 : heading.includes(q) ? 2 : -1;
        return { node, score };
      })
      .filter((r) => r.score >= 0)
      .sort((a, b) => a.score - b.score || a.node.number.length - b.node.number.length)
      .slice(0, 12)
      .map((r) => ({
        kind: 'clause' as const,
        label: r.node.number,
        hint: r.node.heading ?? '',
      }));

    const modes = MODES.filter((m) => m.toLowerCase().startsWith(q)).map((mode) => ({
      kind: 'mode' as const,
      label: mode,
      hint: 'Go to',
    }));

    return [...modes, ...clauses];
  }, [query, nodes]);

  /*
   * Two characters before asking. One character matches most of the corpus and
   * the answer is useless; the round trip is not free even when it is fast.
   */
  const q = query.trim();
  const text = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.search(q),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });

  /*
   * Text hits the number-and-heading pass already found are dropped. Showing
   * clause 20 twice — once because the heading matched, once because the body
   * did — makes the palette look broken to the only person who would notice.
   */
  const seen = new Set(results.filter((r) => r.kind === 'clause').map((r) => r.label));
  const textResults = (text.data?.results ?? []).filter((r) => !seen.has(r.number)).slice(0, 8);

  useEffect(() => setCursor(0), [query]);

  /*
   * One cursor across both lists, because they are one list to the person
   * arrowing through them. Text results sit after the local ones, so the index
   * past `results.length` addresses them.
   */
  const totalRows = results.length + textResults.length;

  const choose = (index: number): void => {
    const local = results[index];
    if (local) {
      if (local.kind === 'mode') onMode(local.label as Mode);
      else onClause(local.label);
      onClose();
      return;
    }
    const hit = textResults[index - results.length];
    if (!hit) return;
    onClause(hit.number);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]"
      style={{ background: 'rgba(42,42,40,0.28)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="panel w-[min(560px,92vw)] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-label="Command palette"
      >
        <input
          ref={input}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setCursor((c) => Math.min(c + 1, totalRows - 1));
            }
            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setCursor((c) => Math.max(c - 1, 0));
            }
            if (event.key === 'Enter') choose(cursor);
          }}
          placeholder="Clause number, heading, a phrase in the text, or a mode"
          aria-label="Search"
          className="w-full border-b px-3 py-2.5 text-[13px] outline-none"
          style={{ borderColor: 'var(--color-rule)', background: 'transparent' }}
        />
        <ul className="max-h-[46vh] overflow-y-auto">
          {results.map((result, index) => (
            <li key={`${result.kind}-${result.label}`}>
              <button
                onMouseEnter={() => setCursor(index)}
                onClick={() => choose(index)}
                className="hit flex w-full items-baseline gap-2 px-3 py-1.5 text-left"
                style={{ background: index === cursor ? 'var(--color-subtle)' : 'transparent' }}
              >
                <span className="clause-number shrink-0 text-[11px]">{result.label}</span>
                <span className="truncate text-[12px]" style={{ color: 'var(--color-muted)' }}>
                  {result.hint}
                </span>
              </button>
            </li>
          ))}
          {/*
            The text hits, under a rule and a label so it is obvious they are a
            different kind of match. A number match is certain; a body match is
            "these words appear here", and running them together as one list
            would make the second look as authoritative as the first.
          */}
          {textResults.length > 0 && (
            <>
              <li className="label border-t px-3 pb-1 pt-2 text-[10px]"
                  style={{ borderColor: 'var(--color-rule)', color: 'var(--color-muted)' }}>
                In the text
              </li>
              {textResults.map((hit, offset) => {
                const index = results.length + offset;
                return (
                  <li key={`text-${hit.instrument}-${hit.number}`}>
                    <button
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => choose(index)}
                      className="hit flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left"
                      style={{ background: index === cursor ? 'var(--color-subtle)' : 'transparent' }}
                    >
                      <span className="flex w-full items-baseline gap-2">
                        <span className="clause-number shrink-0 text-[11px]">{hit.number}</span>
                        <span className="truncate text-[12px]" style={{ color: 'var(--color-muted)' }}>
                          {hit.heading ?? ''}
                        </span>
                      </span>
                      {/*
                        The snippet is why this is worth having. A list of clause
                        numbers makes somebody open each one to find the right
                        one; the matched words in context is what turns results
                        into an answer. Postgres marks them with << >>, rendered
                        as text rather than as HTML — the corpus is a legal
                        instrument and nothing from it should ever reach
                        dangerouslySetInnerHTML.
                      */}
                      <span className="line-clamp-2 text-[11.5px] leading-snug"
                            style={{ color: 'var(--color-muted)' }}>
                        {hit.snippet.replace(/<<|>>/g, '')}
                      </span>
                    </button>
                  </li>
                );
              })}
            </>
          )}

          {results.length === 0 && textResults.length === 0 ? (
            <li className="px-3 py-3 text-[12px]" style={{ color: 'var(--color-muted)' }}>
              {text.isFetching
                ? 'Searching the text…'
                : 'Nothing matches that. Clause numbers look like 24.9.1(b).'}
            </li>
          ) : null}
        </ul>
      </div>
    </div>
  );
}
